import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Heart, Bookmark, Trash2, Sparkles, Play, Layers, Camera, ShoppingBag, ExternalLink, X, Edit3, Eye, Star, Palette, Download, CalendarDays } from 'lucide-react';
import { Button } from "../components/ui/button";
import { Skeleton } from "../components/ui/skeleton";
import { useToast } from '../components/ui/use-toast';
import { getApiUrl, getCurrencySymbol } from '../utils';
import { cacheSet, cacheGet } from '../utils/offlineCache';
import { Filesystem, Directory } from '@capacitor/filesystem';
import { Share } from '@capacitor/share';

const API_URL = getApiUrl();

// Source definitions with labels, icons, and colors
const SOURCE_CONFIG = {
    'all': { label: 'All', icon: null, color: '' },
    'favorites': { label: 'Favorites', icon: Heart, color: 'text-red-500', badge: 'bg-red-50 text-red-600 border-red-200' },
    'ai-studio': { label: 'AI Studio', icon: Sparkles, color: 'text-amber-500', badge: 'bg-amber-50 text-amber-700 border-amber-200' },
    'virtual-tryon': { label: 'Try-On', icon: Camera, color: 'text-purple-500', badge: 'bg-purple-50 text-purple-700 border-purple-200' },
    'product-search': { label: 'Products', icon: ShoppingBag, color: 'text-emerald-500', badge: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
    'stickers': { label: 'Stickers', icon: Sparkles, color: 'text-pink-500', badge: 'bg-pink-50 text-pink-700 border-pink-200' },
    'outfits': { label: 'Outfits', icon: Layers, color: 'text-suede-500', badge: 'bg-suede-50 text-suede-700 border-suede-200' },
    'saved-looks': { label: 'Saved Canvases', icon: Palette, color: 'text-indigo-500', badge: 'bg-indigo-50 text-indigo-700 border-indigo-200' },
    'calendar-saves': { label: 'Calendar Saves', icon: CalendarDays, color: 'text-olive-500', badge: 'bg-olive-50 text-olive-700 border-olive-200' },
};

function getSourceForItem(item) {
    if (item.style === 'outfit-board') return 'saved-looks';
    if (item.style === 'calendar-board') return 'calendar-saves';
    if (item.source && item.source !== 'unknown') return item.source;
    if (item.brand === 'AI Studio' && item.style === 'tryon') return 'virtual-tryon';
    if (item.brand === 'AI Studio') return 'ai-studio';
    if (item.brand === 'Stickers') return 'stickers';
    return 'product-search';
}

export default function Saved() {
    const [savedItems, setSavedItems] = useState([]);
    const [outfits, setOutfits] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [activeSource, setActiveSource] = useState('all');
    const [selectedItem, setSelectedItem] = useState(null);
    const [selectedOutfit, setSelectedOutfit] = useState(null);
    const [editingNotes, setEditingNotes] = useState(false);
    const [notesText, setNotesText] = useState('');
    const token = localStorage.getItem('token');
    const { toast } = useToast();
    const navigate = useNavigate();

    // Load cached data immediately
    useEffect(() => {
        cacheGet('saved_items').then(cached => {
            if (cached?.data) {
                if (cached.data.items?.length) { setSavedItems(cached.data.items); setIsLoading(false); }
                if (cached.data.outfits) setOutfits(cached.data.outfits);
            }
        });
    }, []);

    // Fetch saved items + outfits
    const fetchSavedItems = async () => {
        if (!token) {
            setIsLoading(false);
            return;
        }
        try {
            const [savedRes, outfitsRes] = await Promise.all([
                axios.get(`${API_URL}/saved`, { headers: { Authorization: `Bearer ${token}` } }),
                axios.get(`${API_URL}/real-wardrobe/outfits`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: { outfits: [] } }))
            ]);
            const data = savedRes.data;
            setSavedItems(Array.isArray(data) ? data : (data.items || []));
            setOutfits(outfitsRes.data?.outfits || []);

            // Cache for offline use
            cacheSet('saved_items', {
                items: Array.isArray(data) ? data : (data.items || []),
                outfits: outfitsRes.data?.outfits || []
            });
        } catch (error) {
            console.error('Error fetching saved items:', error);
            // Fallback to cache on error
            const cached = await cacheGet('saved_items');
            if (cached?.data?.items) setSavedItems(cached.data.items);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchSavedItems();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Lock body scroll when any modal is open (prevents iOS background scroll)
    useEffect(() => {
        if (selectedItem || selectedOutfit) {
            document.body.style.overflow = 'hidden';
            document.body.style.position = 'fixed';
            document.body.style.width = '100%';
            document.body.style.top = `-${window.scrollY}px`;
        } else {
            const scrollY = document.body.style.top;
            document.body.style.overflow = '';
            document.body.style.position = '';
            document.body.style.width = '';
            document.body.style.top = '';
            window.scrollTo(0, parseInt(scrollY || '0') * -1);
        }
        return () => {
            document.body.style.overflow = '';
            document.body.style.position = '';
            document.body.style.width = '';
            document.body.style.top = '';
        };
    }, [selectedItem, selectedOutfit]);

    // Remove saved item
    const handleRemove = async (productId) => {
        try {
            await axios.delete(`${API_URL}/saved/${productId}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setSavedItems(savedItems.filter(item => item.product_id !== productId));
            if (selectedItem?.product_id === productId) setSelectedItem(null);
            toast({ title: '✅ Removed', description: 'Item removed from saved' });
        } catch (error) {
            console.error('Error removing item:', error);
        }
    };

    // Toggle favorite
    const handleToggleFavorite = async (productId) => {
        try {
            const res = await axios.patch(`${API_URL}/saved/${productId}/favorite`, {}, {
                headers: { Authorization: `Bearer ${token}` }
            });
            const isFav = res.data.is_favorite;
            setSavedItems(prev => prev.map(item =>
                item.product_id === productId ? { ...item, is_favorite: isFav } : item
            ));
            if (selectedItem?.product_id === productId) {
                setSelectedItem(prev => ({ ...prev, is_favorite: isFav }));
            }
            toast({ title: isFav ? '❤️ Favorited' : '💔 Unfavorited' });
        } catch (error) {
            console.error('Error toggling favorite:', error);
        }
    };

    // Save notes
    const handleSaveNotes = async (productId) => {
        try {
            await axios.patch(`${API_URL}/saved/${productId}/notes`, { notes: notesText }, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setSavedItems(prev => prev.map(item =>
                item.product_id === productId ? { ...item, saved_notes: notesText } : item
            ));
            if (selectedItem?.product_id === productId) {
                setSelectedItem(prev => ({ ...prev, saved_notes: notesText }));
            }
            setEditingNotes(false);
            toast({ title: '✅ Notes saved' });
        } catch (error) {
            console.error('Error saving notes:', error);
        }
    };

    // Download saved item (video or image) to device
    const handleDownload = async (item) => {
        if (!item?.image_url) return;
        const url = item.image_url;
        const isVid = isVideo(url);
        const ext = isVid ? 'mp4' : 'png';
        const fileName = `saved-${isVid ? 'video' : 'image'}-${Date.now()}.${ext}`;

        try {
            toast({ title: '⬇️ Downloading...', description: 'Saving to your device' });

            const isNative = window.Capacitor?.isNativePlatform?.();

            if (isNative) {
                const response = await fetch(url);
                const blob = await response.blob();
                const base64 = await new Promise((resolve) => {
                    const reader = new FileReader();
                    reader.onloadend = () => resolve(reader.result.split(',')[1]);
                    reader.readAsDataURL(blob);
                });

                const savedFile = await Filesystem.writeFile({
                    path: fileName,
                    data: base64,
                    directory: Directory.Cache,
                });

                await Share.share({
                    title: item.product_name || 'Saved Item',
                    url: savedFile.uri,
                });

                toast({ title: '✅ Ready to save!', description: 'Use the share sheet to save to your Photos' });
            } else {
                const response = await fetch(url);
                const blob = await response.blob();
                const blobUrl = URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = blobUrl;
                link.download = fileName;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                URL.revokeObjectURL(blobUrl);
                toast({ title: '✅ Downloaded!', description: `${fileName} saved` });
            }
        } catch (err) {
            console.error('Download failed:', err);
            toast({ title: '⚠️ Download failed', description: 'Please try again', variant: 'destructive' });
        }
    };

    // Delete outfit
    const handleDeleteOutfit = async (outfitId, e) => {
        if (e) e.stopPropagation();
        try {
            await axios.delete(`${API_URL}/real-wardrobe/outfits/${outfitId}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setOutfits(prev => prev.filter(o => o.id !== outfitId));
            if (selectedOutfit?.id === outfitId) setSelectedOutfit(null);
            toast({ title: '✅ Deleted', description: 'Outfit removed' });
        } catch (error) {
            console.error('Error deleting outfit:', error);
            toast({ title: '❌ Error', description: 'Could not delete outfit', variant: 'destructive' });
        }
    };

    // Get all images from an outfit object
    const getOutfitImages = (outfit) => {
        return [outfit.top_image, outfit.bottom_image, outfit.dress_image, outfit.shoes_image, outfit.outerwear_image, outfit.bag_image].filter(Boolean);
    };

    // Check if URL is a video
    const isVideo = (url) => {
        if (!url) return false;
        return url.match(/\.(mp4|webm|mov)$/i) || url.includes('replicate.delivery');
    };

    // Count items by source
    const sourceCounts = {};
    savedItems.forEach(item => {
        const source = getSourceForItem(item);
        sourceCounts[source] = (sourceCounts[source] || 0) + 1;
    });

    // Count favorites across all items + outfits
    const favoritesCount = savedItems.filter(i => i.is_favorite).length + outfits.filter(o => o.is_favorite).length;

    // Build source filter tabs
    const availableSources = ['all'];
    if (favoritesCount > 0) availableSources.push('favorites');
    if (sourceCounts['ai-studio']) availableSources.push('ai-studio');
    if (sourceCounts['virtual-tryon']) availableSources.push('virtual-tryon');
    if (sourceCounts['product-search']) availableSources.push('product-search');
    if (sourceCounts['stickers']) availableSources.push('stickers');
    if (outfits.length > 0) availableSources.push('outfits');
    if (sourceCounts['saved-looks']) availableSources.push('saved-looks');
    if (sourceCounts['calendar-saves']) availableSources.push('calendar-saves');

    // Filter items by active source
    // saved-looks and calendar-saves are ALWAYS excluded — their dedicated sections handle rendering
    const filteredItems = activeSource === 'favorites'
        ? savedItems.filter(item => item.is_favorite)
        : savedItems.filter(item => {
            const src = getSourceForItem(item);
            if (src === 'saved-looks' || src === 'calendar-saves') return false;
            if (activeSource === 'all' || activeSource === 'outfits') return true;
            return src === activeSource;
        });

    // Saved looks filtered separately for their own section
    const savedLooks = savedItems.filter(item => getSourceForItem(item) === 'saved-looks');

    // Calendar saves filtered separately for their own section
    const calendarSaves = savedItems.filter(item => getSourceForItem(item) === 'calendar-saves');

    const totalCount = savedItems.length + outfits.length;

    // If user not logged in
    if (!token) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-[#faf9f7]">
                <div className="text-center p-12 rounded-2xl bg-white/60 border border-stone-100">
                    <div className="w-16 h-16 rounded-full border border-stone-300 flex items-center justify-center mx-auto mb-4">
                        <Heart className="w-8 h-8 text-stone-500" />
                    </div>
                    <h2 className="text-2xl font-extralight mb-2 text-stone-800" style={{ fontFamily: 'Georgia, serif' }}>Sign in to see your <em className="italic font-light text-stone-600">saved items</em></h2>
                    <p className="text-gray-500 mb-6">Save items you love and find them here</p>
                    <a href="/login">
                        <Button className="bg-stone-800 hover:bg-stone-900 text-white rounded-full px-8 text-xs uppercase tracking-[0.15em]">
                            Sign In
                        </Button>
                    </a>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[#faf9f7]">
            {/* Header */}
            <div className="border-b border-stone-200">
                <div className="max-w-7xl mx-auto px-6 py-10">
                    <div className="flex items-center gap-4 mb-2">
                        <div className="w-12 h-12 rounded-full border border-stone-300 flex items-center justify-center">
                            <Heart className="w-5 h-5 text-stone-600" />
                        </div>
                        <div>
                            <p className="text-[10px] uppercase tracking-[0.2em] text-stone-400 mb-0.5">Collection</p>
                            <h1 className="text-2xl font-extralight text-stone-800" style={{ fontFamily: 'Georgia, serif' }}>Saved <em className="italic font-light text-stone-600">Items</em></h1>
                            <p className="text-sm text-stone-500">
                                {totalCount} {totalCount === 1 ? 'piece' : 'pieces'} in your collection
                                {outfits.length > 0 && ` · ${outfits.length} outfit${outfits.length !== 1 ? 's' : ''}`}
                            </p>
                        </div>
                    </div>
                </div>
            </div>
            
            <div className="max-w-7xl mx-auto px-6 py-8">
                {/* Source Filter Tabs */}
                {!isLoading && totalCount > 0 && availableSources.length > 2 && (
                    <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
                        {availableSources.map(source => {
                            const config = SOURCE_CONFIG[source];
                            const count = source === 'all' ? savedItems.length 
                                        : source === 'favorites' ? favoritesCount
                                        : source === 'outfits' ? outfits.length 
                                        : (sourceCounts[source] || 0);
                            return (
                                <button
                                    key={source}
                                    onClick={() => setActiveSource(source)}
                                    className={`px-4 py-2 rounded-full text-xs font-medium uppercase tracking-[0.1em] whitespace-nowrap transition-all flex items-center gap-1.5 ${
                                        activeSource === source
                                            ? 'bg-stone-800 text-white'
                                            : 'bg-stone-100 text-stone-500 hover:bg-stone-200'
                                    }`}
                                >
                                    {config.icon && <config.icon className="w-3.5 h-3.5" />}
                                    {config.label} ({count})
                                </button>
                            );
                        })}
                    </div>
                )}

                {/* Favorited Outfits in Favorites Tab */}
                {!isLoading && activeSource === 'favorites' && outfits.filter(o => o.is_favorite).length > 0 && (
                    <div className="mb-10">
                        <div className="flex items-center gap-3 mb-4">
                            <Layers className="w-5 h-5 text-suede-500" />
                            <h2 className="text-lg font-medium text-stone-800">Favorited Outfits</h2>
                            <span className="text-xs px-2 py-0.5 rounded-full bg-red-50 text-red-600">{outfits.filter(o => o.is_favorite).length}</span>
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 lg:gap-6">
                            {outfits.filter(o => o.is_favorite).map((outfit) => (
                                <motion.div
                                    key={outfit.id}
                                    initial={{ opacity: 0, scale: 0.95 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    className="group relative bg-white rounded-2xl border border-stone-100 overflow-hidden hover:shadow-lg transition-all cursor-pointer active:scale-[0.97]"
                                    onClick={() => setSelectedOutfit(outfit)}
                                >
                                    <div className="aspect-square bg-stone-50 p-3 grid grid-cols-2 gap-1.5">
                                        {[outfit.top_image, outfit.bottom_image, outfit.dress_image, outfit.shoes_image, outfit.outerwear_image, outfit.bag_image]
                                            .filter(Boolean)
                                            .slice(0, 4)
                                            .map((img, i) => (
                                                <div key={i} className="rounded-lg overflow-hidden bg-white">
                                                    <img src={img} alt="" className="w-full h-full object-cover" />
                                                </div>
                                            ))
                                        }
                                    </div>
                                    <div className="p-4">
                                        <div className="flex items-center gap-1.5 mb-1">
                                            <Heart className="w-3 h-3 text-red-400 fill-red-400" />
                                            <span className={`text-[10px] px-1.5 py-0.5 rounded-full border ${SOURCE_CONFIG['outfits'].badge}`}>Outfit</span>
                                        </div>
                                        <h3 className="font-medium text-stone-900 line-clamp-1">{outfit.name}</h3>
                                    </div>
                                    <button
                                        onClick={(e) => handleDeleteOutfit(outfit.id, e)}
                                        className="absolute top-2 right-2 w-8 h-8 rounded-full bg-black/40 backdrop-blur-sm flex items-center justify-center opacity-70 hover:opacity-100 transition-opacity active:scale-90"
                                    >
                                        <Trash2 className="w-4 h-4 text-white" />
                                    </button>
                                </motion.div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Outfits Section */}
                {!isLoading && outfits.length > 0 && (activeSource === 'all' || activeSource === 'outfits') && (
                    <div className="mb-10">
                        <div className="flex items-center gap-3 mb-4">
                            <Layers className="w-5 h-5 text-suede-500" />
                            <h2 className="text-lg font-medium text-stone-800">Saved Outfits</h2>
                            <span className="text-xs px-2 py-0.5 rounded-full bg-suede-100 text-suede-600">{outfits.length}</span>
                            <p className="text-xs text-stone-400 ml-auto">From Outfit Builder · My Wardrobe</p>
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 lg:gap-6">
                            {outfits.map((outfit) => (
                                <motion.div
                                    key={outfit.id}
                                    initial={{ opacity: 0, scale: 0.95 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    className="group relative bg-white rounded-2xl border border-stone-100 overflow-hidden hover:shadow-lg transition-all cursor-pointer active:scale-[0.97]"
                                    onClick={() => setSelectedOutfit(outfit)}
                                >
                                    <div className="aspect-square bg-stone-50 p-3 grid grid-cols-2 gap-1.5">
                                        {[outfit.top_image, outfit.bottom_image, outfit.dress_image, outfit.shoes_image, outfit.outerwear_image, outfit.bag_image]
                                            .filter(Boolean)
                                            .slice(0, 4)
                                            .map((img, i) => (
                                                <div key={i} className="rounded-lg overflow-hidden bg-white">
                                                    <img src={img} alt="" className="w-full h-full object-cover" />
                                                </div>
                                            ))
                                        }
                                        {[outfit.top_image, outfit.bottom_image, outfit.dress_image, outfit.shoes_image].filter(Boolean).length === 0 && (
                                            <div className="col-span-2 row-span-2 flex items-center justify-center">
                                                <Layers className="w-10 h-10 text-stone-300" />
                                            </div>
                                        )}
                                    </div>
                                    <div className="p-4">
                                        <div className="flex items-center gap-1.5 mb-1">
                                            <span className={`text-[10px] px-1.5 py-0.5 rounded-full border ${SOURCE_CONFIG['outfits'].badge}`}>
                                                Outfit Builder
                                            </span>
                                            {outfit.is_favorite && <Heart className="w-3 h-3 text-red-400 fill-red-400" />}
                                        </div>
                                        <h3 className="font-medium text-stone-900 line-clamp-1">{outfit.name}</h3>
                                        {outfit.occasion && (
                                            <p className="text-xs text-stone-400 mt-0.5">{outfit.occasion}</p>
                                        )}
                                        <p className="text-[10px] text-stone-300 mt-1">
                                            {outfit.times_worn || 0} wears · {new Date(outfit.created_at).toLocaleDateString()}
                                        </p>
                                    </div>
                                    <button
                                        onClick={(e) => handleDeleteOutfit(outfit.id, e)}
                                        className="absolute top-2 right-2 w-8 h-8 rounded-full bg-black/40 backdrop-blur-sm flex items-center justify-center opacity-70 hover:opacity-100 transition-opacity active:scale-90"
                                    >
                                        <Trash2 className="w-4 h-4 text-white" />
                                    </button>
                                </motion.div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Saved Canvases Section (from Outfit Board / Style Studio) */}
                {!isLoading && savedLooks.length > 0 && (activeSource === 'all' || activeSource === 'saved-looks') && (
                    <div className="mb-10">
                        <div className="flex items-center gap-3 mb-4">
                            <Palette className="w-5 h-5 text-indigo-500" />
                            <h2 className="text-lg font-medium text-stone-800">Saved Canvases</h2>
                            <span className="text-xs px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-600">{savedLooks.length}</span>
                            <p className="text-xs text-stone-400 ml-auto">From Outfit Board</p>
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 lg:gap-6">
                            {savedLooks.map((look) => (
                                <motion.div
                                    key={look.product_id}
                                    initial={{ opacity: 0, scale: 0.95 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    className="group relative bg-white rounded-2xl border border-stone-100 overflow-hidden hover:shadow-lg transition-all cursor-pointer active:scale-[0.97]"
                                    onClick={() => setSelectedItem(look)}
                                >
                                    <div className="aspect-square bg-stone-100 overflow-hidden">
                                        {look.image_url ? (
                                            <img src={look.image_url} alt={look.product_name || 'Saved look'} className="w-full h-full object-contain" />
                                        ) : (
                                            <div className="w-full h-full flex items-center justify-center">
                                                <Palette className="w-10 h-10 text-stone-300" />
                                            </div>
                                        )}
                                    </div>
                                    <div className="p-4">
                                        <span className={`text-[10px] px-1.5 py-0.5 rounded-full border inline-block mb-1.5 ${SOURCE_CONFIG['saved-looks'].badge}`}>
                                            Saved Look
                                        </span>
                                        <h3 className="font-medium text-stone-900 line-clamp-1">
                                            {look.product_name || 'Style Studio Look'}
                                        </h3>
                                        <p className="text-[10px] text-stone-300 mt-1">
                                            {look.category || 'Outfit'} · {look.saved_at ? new Date(look.saved_at).toLocaleDateString() : ''}
                                        </p>
                                    </div>
                                    <button
                                        onClick={(e) => { e.stopPropagation(); handleRemove(look.product_id); }}
                                        className="absolute top-2 right-2 w-8 h-8 rounded-full bg-black/40 backdrop-blur-sm flex items-center justify-center opacity-70 hover:opacity-100 transition-opacity active:scale-90"
                                    >
                                        <Trash2 className="w-4 h-4 text-white" />
                                    </button>
                                </motion.div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Calendar Saves Section */}
                {!isLoading && calendarSaves.length > 0 && (activeSource === 'all' || activeSource === 'calendar-saves') && (
                    <div className="mb-10">
                        <div className="flex items-center gap-3 mb-4">
                            <CalendarDays className="w-5 h-5 text-olive-500" />
                            <h2 className="text-lg font-medium text-stone-800">Calendar Saves</h2>
                            <span className="text-xs px-2 py-0.5 rounded-full bg-olive-100 text-olive-600">{calendarSaves.length}</span>
                            <p className="text-xs text-stone-400 ml-auto">From Outfit Calendar</p>
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 lg:gap-6">
                            {calendarSaves.map((item) => (
                                <motion.div
                                    key={item.product_id}
                                    initial={{ opacity: 0, scale: 0.95 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    className="group relative bg-white rounded-2xl border border-stone-100 overflow-hidden hover:shadow-lg transition-all cursor-pointer active:scale-[0.97]"
                                    onClick={() => setSelectedItem(item)}
                                >
                                    <div className="aspect-square bg-stone-100 overflow-hidden">
                                        {item.image_url ? (
                                            <img src={item.image_url} alt={item.product_name || 'Calendar save'} className="w-full h-full object-contain" />
                                        ) : (
                                            <div className="w-full h-full flex items-center justify-center">
                                                <CalendarDays className="w-10 h-10 text-stone-300" />
                                            </div>
                                        )}
                                    </div>
                                    <div className="p-4">
                                        <span className={`text-[10px] px-1.5 py-0.5 rounded-full border inline-block mb-1.5 ${SOURCE_CONFIG['calendar-saves'].badge}`}>
                                            Calendar Save
                                        </span>
                                        <h3 className="font-medium text-stone-900 line-clamp-1">
                                            {item.product_name || 'Calendar Outfit'}
                                        </h3>
                                        <p className="text-[10px] text-stone-300 mt-1">
                                            {item.category || 'Outfit'} · {item.saved_at ? new Date(item.saved_at).toLocaleDateString() : ''}
                                        </p>
                                    </div>
                                    <button
                                        onClick={(e) => { e.stopPropagation(); handleRemove(item.product_id); }}
                                        className="absolute top-2 right-2 w-8 h-8 rounded-full bg-black/40 backdrop-blur-sm flex items-center justify-center opacity-70 hover:opacity-100 transition-opacity active:scale-90"
                                    >
                                        <Trash2 className="w-4 h-4 text-white" />
                                    </button>
                                </motion.div>
                            ))}
                        </div>
                    </div>
                )}

                {isLoading ? (
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 lg:gap-6">
                        {Array(4).fill(0).map((_, i) => (
                            <div key={i} className="space-y-3 bg-white p-3 rounded-2xl border border-stone-100">
                                <Skeleton className="aspect-[3/4] rounded-2xl" />
                                <Skeleton className="h-4 w-1/3" />
                                <Skeleton className="h-5 w-2/3" />
                            </div>
                        ))}
                    </div>
                ) : filteredItems.length === 0 && activeSource !== 'outfits' && activeSource !== 'saved-looks' && activeSource !== 'calendar-saves' && (activeSource !== 'favorites' || favoritesCount === 0) && savedLooks.length === 0 && calendarSaves.length === 0 ? (
                    <div className="text-center py-20 bg-white/50 rounded-2xl border border-stone-100">
                        <div className="w-24 h-24 rounded-full border border-stone-300 flex items-center justify-center mx-auto mb-6">
                            {SOURCE_CONFIG[activeSource]?.icon ? 
                                React.createElement(SOURCE_CONFIG[activeSource].icon, { className: 'w-10 h-10 text-stone-400' }) :
                                <Bookmark className="w-10 h-10 text-stone-400" />
                            }
                        </div>
                        <h3 className="text-xl font-extralight text-stone-800 mb-2" style={{ fontFamily: 'Georgia, serif' }}>
                            {activeSource === 'all' ? 'No saved items ' : `No ${SOURCE_CONFIG[activeSource]?.label || activeSource} items `}
                            <em className="italic font-light text-stone-600">yet</em>
                        </h3>
                        <p className="text-stone-500 max-w-sm mx-auto">
                            {activeSource === 'all' 
                                ? 'Start exploring and save pieces you love to build your personal collection.'
                                : `Save items from ${SOURCE_CONFIG[activeSource]?.label || activeSource} and they'll appear here.`
                            }
                        </p>
                    </div>
                ) : (
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 lg:gap-6">
                            {filteredItems.map((item, index) => (
                                <div
                                    key={item.product_id}
                                    className="group relative bg-white rounded-2xl border border-stone-100 overflow-hidden hover:shadow-lg transition-all cursor-pointer active:scale-[0.98]"
                                    onClick={() => {
                                        setSelectedItem(item);
                                        setNotesText(item.saved_notes || '');
                                        setEditingNotes(false);
                                    }}
                                >
                                    <div className="aspect-[3/4] overflow-hidden bg-stone-50 relative">
                                        {item.image_url && isVideo(item.image_url) ? (
                                            <>
                                                <video
                                                    src={`${item.image_url}#t=0.1`}
                                                    className="w-full h-full object-cover pointer-events-none"
                                                    muted
                                                    loop
                                                    playsInline
                                                    preload="metadata"
                                                />
                                                <div className="absolute top-2 left-2 w-7 h-7 bg-black/50 rounded-full flex items-center justify-center pointer-events-none">
                                                    <Play className="w-3.5 h-3.5 text-white fill-white" />
                                                </div>
                                            </>
                                        ) : item.image_url ? (
                                            <img
                                                src={item.image_url}
                                                alt={item.product_name}
                                                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                                            />
                                        ) : (
                                            <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-suede-50 to-terracotta-50">
                                                <Heart className="w-12 h-12 text-suede-400" />
                                            </div>
                                        )}
                                        {/* Favorite indicator */}
                                        {item.is_favorite && (
                                            <div className="absolute top-2 left-2 pointer-events-none">
                                                <Heart className="w-5 h-5 text-red-500 fill-red-500 drop-shadow" />
                                            </div>
                                        )}
                                        {(() => {
                                            const src = getSourceForItem(item);
                                            const srcConf = SOURCE_CONFIG[src];
                                            return srcConf ? (
                                                <div className="absolute bottom-2 left-2 px-2 py-0.5 bg-black/50 rounded-full flex items-center gap-1 pointer-events-none">
                                                    {srcConf.icon && <srcConf.icon className="w-3 h-3 text-white" />}
                                                    <span className="text-[10px] text-white font-medium">{srcConf.label}</span>
                                                </div>
                                            ) : null;
                                        })()}
                                    </div>
                                    <div className="p-4">
                                        {(() => {
                                            const src = getSourceForItem(item);
                                            const srcConf = SOURCE_CONFIG[src];
                                            return srcConf?.badge ? (
                                                <span className={`text-[10px] px-1.5 py-0.5 rounded-full border inline-block mb-1.5 ${srcConf.badge}`}>
                                                    {srcConf.label}
                                                </span>
                                            ) : null;
                                        })()}
                                        <p className="text-xs text-stone-500 mb-1">{item.brand || item.category}</p>
                                        <h3 className="font-medium text-stone-900 mb-1 line-clamp-2">{item.product_name}</h3>
                                        <div className="flex items-center justify-between">
                                            {item.price && (
                                                <p className="text-sm font-semibold text-suede-600">{getCurrencySymbol()}{parseFloat(item.price).toFixed(2)}</p>
                                            )}
                                            {item.store_url && (
                                                <a href={item.store_url} target="_blank" rel="noopener noreferrer" className="text-stone-400 hover:text-stone-600" onClick={e => e.stopPropagation()}>
                                                    <ExternalLink className="w-3.5 h-3.5" />
                                                </a>
                                            )}
                                        </div>
                                    </div>
                                    <button
                                        onClick={(e) => { e.stopPropagation(); handleRemove(item.product_id); }}
                                        className="absolute top-2 right-2 w-8 h-8 rounded-full bg-black/40 backdrop-blur-sm flex items-center justify-center opacity-70 hover:opacity-100 transition-opacity active:scale-90"
                                    >
                                        <Trash2 className="w-4 h-4 text-white" />
                                    </button>
                                </div>
                            ))}
                    </div>
                )}
            </div>

            {/* Saved Item Detail — full-screen modal */}
            <AnimatePresence>
                {selectedItem && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-50 bg-white"
                        style={{ paddingTop: 'env(safe-area-inset-top)', paddingBottom: 'env(safe-area-inset-bottom)' }}
                    >
                        {/* Sticky header bar */}
                        <div className="sticky top-0 z-30 flex items-center justify-between px-4 py-2 bg-white border-b border-stone-100" style={{ zIndex: 30 }}>
                            <button
                                onClick={() => { setSelectedItem(null); setEditingNotes(false); }}
                                className="flex items-center gap-1.5 text-stone-600 active:text-stone-900 min-h-[44px] min-w-[44px] px-2 -ml-2"
                            >
                                <X className="w-6 h-6" />
                                <span className="text-sm font-medium">Close</span>
                            </button>
                            {(() => {
                                const src = getSourceForItem(selectedItem);
                                const srcConf = SOURCE_CONFIG[src];
                                return srcConf?.badge ? (
                                    <span className={`text-[10px] px-2.5 py-1 rounded-full border ${srcConf.badge}`}>
                                        {srcConf.label}
                                    </span>
                                ) : null;
                            })()}
                        </div>

                        {/* Scrollable content */}
                        <div
                            className="h-full overflow-y-auto"
                            style={{ WebkitOverflowScrolling: 'touch', overscrollBehavior: 'contain', paddingBottom: '120px' }}
                        >
                            {/* Image — properly contained with checkerboard bg for transparent canvases */}
                            <div className="w-full flex items-center justify-center bg-stone-100" style={{ maxHeight: '45vh', minHeight: '180px' }}>
                                {selectedItem.image_url && isVideo(selectedItem.image_url) ? (
                                    <video
                                        src={selectedItem.image_url}
                                        className="w-full h-full object-contain"
                                        style={{ maxHeight: '45vh' }}
                                        controls
                                        autoPlay
                                        muted
                                        loop
                                        playsInline
                                    />
                                ) : selectedItem.image_url ? (
                                    <img
                                        src={selectedItem.image_url}
                                        alt={selectedItem.product_name}
                                        className="w-full h-full object-contain"
                                        style={{ maxHeight: '45vh' }}
                                    />
                                ) : (
                                    <div className="w-full h-48 flex items-center justify-center bg-gradient-to-br from-suede-50 to-terracotta-50">
                                        <Heart className="w-16 h-16 text-suede-400" />
                                    </div>
                                )}
                            </div>

                            {/* Details & actions */}
                            <div className="px-5 pt-4">
                                <p className="text-xs text-stone-500 mb-0.5">{selectedItem.brand || selectedItem.category}</p>
                                <h2 className="text-lg font-semibold text-stone-900 mb-1">{selectedItem.product_name}</h2>

                                {selectedItem.price && (
                                    <p className="text-lg font-bold text-suede-600 mb-3">
                                        {getCurrencySymbol()}{parseFloat(selectedItem.price).toFixed(2)}
                                    </p>
                                )}

                                {/* Notes section */}
                                <div className="mb-4 bg-stone-50 rounded-2xl p-3.5">
                                    <div className="flex items-center justify-between mb-2">
                                        <span className="text-xs font-semibold text-stone-500 uppercase tracking-wider flex items-center gap-1.5">
                                            <Edit3 className="w-3.5 h-3.5" /> Notes
                                        </span>
                                        <button
                                            onClick={() => {
                                                if (editingNotes) {
                                                    handleSaveNotes(selectedItem.product_id);
                                                } else {
                                                    setEditingNotes(true);
                                                }
                                            }}
                                            className="text-xs text-suede-600 font-semibold px-3 py-1.5 rounded-lg bg-white border border-stone-200 active:bg-stone-100"
                                        >
                                            {editingNotes ? 'Save' : 'Edit'}
                                        </button>
                                    </div>
                                    {editingNotes ? (
                                        <textarea
                                            value={notesText}
                                            onChange={(e) => setNotesText(e.target.value)}
                                            placeholder="Add notes about this item..."
                                            className="w-full px-3 py-2.5 rounded-xl border border-stone-200 text-sm focus:border-suede-500 outline-none resize-none bg-white"
                                            rows={3}
                                            autoFocus
                                        />
                                    ) : (
                                        <p className="text-sm text-stone-600 min-h-[36px]">
                                            {selectedItem.saved_notes || <span className="text-stone-400 italic">Tap Edit to add notes...</span>}
                                        </p>
                                    )}
                                </div>

                                <p className="text-[10px] text-stone-400 mb-4">
                                    Saved {selectedItem.saved_at ? new Date(selectedItem.saved_at).toLocaleDateString() : ''}
                                </p>

                                {/* Action buttons — large mobile tap targets */}
                                <div className="mb-3">
                                    <button
                                        onClick={() => handleToggleFavorite(selectedItem.product_id)}
                                        className={`w-full flex items-center justify-center gap-2 py-3.5 rounded-2xl border text-sm font-medium transition-all active:scale-[0.97] ${
                                            selectedItem.is_favorite
                                                ? 'bg-red-50 border-red-200 text-red-600'
                                                : 'bg-white border-stone-200 text-stone-700 active:bg-stone-50'
                                        }`}
                                    >
                                        <Heart className={`w-5 h-5 ${selectedItem.is_favorite ? 'fill-red-500' : ''}`} />
                                        {selectedItem.is_favorite ? 'Favorited' : 'Favorite'}
                                    </button>
                                </div>

                                {/* Download button for videos and AI Studio items */}
                                {selectedItem.image_url && (isVideo(selectedItem.image_url) || getSourceForItem(selectedItem) === 'ai-studio') && (
                                    <div className="mb-3">
                                        <button
                                            onClick={() => handleDownload(selectedItem)}
                                            className="w-full flex items-center justify-center gap-2 py-3.5 rounded-2xl bg-stone-800 text-white text-sm font-medium active:bg-stone-900 transition-all active:scale-[0.97]"
                                        >
                                            <Download className="w-5 h-5" />
                                            {isVideo(selectedItem.image_url) ? 'Download Video' : 'Download Image'}
                                        </button>
                                    </div>
                                )}

                                <div className="flex gap-2.5 mb-6">
                                    {selectedItem.store_url && (
                                        <a
                                            href={selectedItem.store_url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="flex-1 flex items-center justify-center gap-2 py-3.5 rounded-2xl bg-stone-800 text-white text-sm font-medium active:bg-stone-900 transition-colors active:scale-[0.97]"
                                        >
                                            <ExternalLink className="w-4 h-4" />
                                            View Product
                                        </a>
                                    )}
                                    <button
                                        onClick={() => handleRemove(selectedItem.product_id)}
                                        className="flex items-center justify-center gap-2 py-3.5 px-5 rounded-2xl border border-red-200 text-red-500 active:bg-red-50 text-sm font-medium transition-colors active:scale-[0.97]"
                                    >
                                        <Trash2 className="w-4 h-4" />
                                        Remove
                                    </button>
                                </div>
                            </div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Outfit Detail — full-screen modal */}
            <AnimatePresence>
                {selectedOutfit && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-50 bg-white md:bg-black/50 md:flex md:items-center md:justify-center"
                        style={{ paddingTop: 'env(safe-area-inset-top)', paddingBottom: 'env(safe-area-inset-bottom)' }}
                        onClick={(e) => { if (e.target === e.currentTarget) setSelectedOutfit(null); }}
                    >
                      <div className="h-full md:h-auto md:max-h-[85vh] md:w-full md:max-w-xl md:rounded-2xl md:overflow-hidden bg-white md:shadow-2xl flex flex-col">
                        {/* Sticky header bar */}
                        <div className="sticky top-0 z-30 flex items-center justify-between px-4 py-2 bg-white border-b border-stone-100 md:rounded-t-2xl" style={{ zIndex: 30 }}>
                            <button
                                onClick={() => setSelectedOutfit(null)}
                                className="flex items-center gap-1.5 text-stone-600 active:text-stone-900 min-h-[44px] min-w-[44px] px-2 -ml-2"
                            >
                                <X className="w-6 h-6" />
                                <span className="text-sm font-medium">Close</span>
                            </button>
                            <span className={`text-[10px] px-2.5 py-1 rounded-full border ${SOURCE_CONFIG['outfits'].badge}`}>
                                Outfit Builder
                            </span>
                        </div>

                        {/* Scrollable content */}
                        <div
                            className="flex-1 overflow-y-auto"
                            style={{ WebkitOverflowScrolling: 'touch', overscrollBehavior: 'contain', paddingBottom: '80px' }}
                        >
                            {/* Outfit images */}
                            <div className="bg-stone-50 p-3 md:p-4">
                                {(() => {
                                    const imgs = getOutfitImages(selectedOutfit);
                                    if (imgs.length === 0) return (
                                        <div className="h-48 flex items-center justify-center">
                                            <Layers className="w-16 h-16 text-stone-300" />
                                        </div>
                                    );
                                    if (imgs.length === 1) return (
                                        <div className="max-h-[50vh] flex justify-center">
                                            <img src={imgs[0]} alt="" className="h-full max-h-[50vh] object-contain rounded-2xl" />
                                        </div>
                                    );
                                    return (
                                        <div className={`grid gap-2 ${imgs.length <= 2 ? 'grid-cols-2' : 'grid-cols-2'}`} style={{ maxHeight: '50vh' }}>
                                            {imgs.slice(0, 4).map((img, i) => (
                                                <div key={i} className="rounded-xl overflow-hidden bg-white aspect-square">
                                                    <img src={img} alt="" className="w-full h-full object-cover" />
                                                </div>
                                            ))}
                                        </div>
                                    );
                                })()}
                            </div>

                            {/* Outfit details */}
                            <div className="px-5 pt-4">
                                <h2 className="text-lg font-semibold text-stone-900 mb-1">{selectedOutfit.name}</h2>
                                {selectedOutfit.occasion && (
                                    <p className="text-sm text-stone-500 mb-1">{selectedOutfit.occasion}</p>
                                )}
                                <p className="text-xs text-stone-400 mb-5">
                                    {selectedOutfit.times_worn || 0} wears · {getOutfitImages(selectedOutfit).length} pieces · {selectedOutfit.created_at ? new Date(selectedOutfit.created_at).toLocaleDateString() : ''}
                                </p>

                                {/* Action buttons */}
                                <div className="flex gap-2.5 mb-6">
                                    <button
                                        onClick={() => {
                                            setSelectedOutfit(null);
                                            navigate('/my-wardrobe');
                                        }}
                                        className="flex-1 flex items-center justify-center gap-2 py-3.5 rounded-2xl bg-stone-800 text-white text-sm font-medium active:bg-stone-900 transition-all active:scale-[0.97]"
                                    >
                                        <Eye className="w-5 h-5" />
                                        View in Wardrobe
                                    </button>
                                    <button
                                        onClick={() => handleDeleteOutfit(selectedOutfit.id)}
                                        className="flex items-center justify-center gap-2 py-3.5 px-5 rounded-2xl border border-red-200 text-red-500 active:bg-red-50 text-sm font-medium transition-colors active:scale-[0.97]"
                                    >
                                        <Trash2 className="w-4 h-4" />
                                        Delete
                                    </button>
                                </div>
                            </div>
                        </div>
                      </div>
                    </motion.div>
                )}
            </AnimatePresence>


        </div>
    );
}
